import { Injectable } from '@angular/core';
import { ArticleConstants } from '@article/constants/article-constants';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ArticleDataService, MountingRailInfo } from '../service/article-data.service';
import { ArrayUtils } from '@shared/util/array-utils';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { ArticleNumberMappingVO } from '../model/vo/article-number-mapping.vo';
import { ArticleNumberMappingUtils } from '../util/article-number-mapping-utils';
import { UserModel } from '@user/model/user.model';
import { ArticleTypeIds } from '../constants/article-type-ids';
import { FavoriteArticleVO } from '@user/components/favorites/models/favorite-article.vo';

export interface RailArticle {
  no: string;
  ownNo: string;
  info: MountingRailInfo;
  railType: string;
}

export interface RailGroup {
  groupName: string;
  railArticles: RailArticle[];
}

@Injectable({
  providedIn: 'root'
})
export class MountingRailService {

  constructor(
    private localeResourceManager: LocaleResourceManager,
    private articleDataService: ArticleDataService,
    private userModel: UserModel,
  ) {
  }

  public loadFavoriteMountingRails(): Observable<FavoriteArticleVO[]> {
    return this.articleDataService.getFavoriteArticles([ ArticleTypeIds.MOUNTING_RAIL ])
      .pipe(
        map((wrappers) => wrappers.at(0)?.favoriteArticles || []),
      );
  }

  public loadMountingRailGroups(dinOrder: Record<string, string>): Observable<RailGroup[]> {
    return this.articleDataService.loadMountingRailInfos()
      .pipe(
        map((infos) => this.createArticleViewList(infos, dinOrder)),
      );
  }

  public getDefaultDinOrder(): Record<string, string> {
    return {
      [ArticleConstants.MOUNTINGRAILTYPE_DIN_35_TS]: 'newproject.din.35',
      [ArticleConstants.MOUNTINGRAILTYPE_DIN_15_TS]: 'newproject.din.15',
      [ArticleConstants.MOUNTINGRAILTYPE_DIN_99_TS]: 'newproject.din.99',
    }
  }

  public getRailArticleLabel(railArticle: RailArticle): string {
    let label: string = railArticle.no;
    if (railArticle.ownNo) {
      label += ` (${ railArticle.ownNo })`;
    }
    if (railArticle.info.displayName) {
      label += ' - ' + railArticle.info.displayName;
    }
    return label;
  }

  private createArticleViewList(mountingRailInfos: MountingRailInfo[], dinOrder: Record<string, string>): RailGroup[] {

    const railGroups: RailGroup[] = [];

    const mountingRailTypes: string[] = Object.keys(dinOrder);

    for (const mountingRailType of mountingRailTypes) {
      const filteredMountingRailInfos: MountingRailInfo[] = mountingRailInfos.filter((info: MountingRailInfo) => {
        return mountingRailType === info.mountingRailType;
      });
      if (ArrayUtils.isNotEmpty(filteredMountingRailInfos)) {
        const railGroup: RailGroup = { groupName: mountingRailType, railArticles: [] };
        railGroups.push(railGroup);
        for (const info of filteredMountingRailInfos) {
          const railArticle: RailArticle = {
            no: this.localeResourceManager.getValueByKey(info.articleNo, undefined, true),
            ownNo: this.getCustomArticleNumberLabel(info),
            info: info,
            railType: mountingRailType,
          };
          railGroup.railArticles.push(railArticle);
        }
      }
    }

    return railGroups;
  }

  private getCustomArticleNumberLabel(info: MountingRailInfo): string {
    const mapping: ArticleNumberMappingVO = ArticleNumberMappingUtils.findMappingByArticleNumber(
      info.articleNo,
      this.userModel.articleNumberMappings
    );
    return mapping ? mapping.customArticleNumber : ``;
  }
}
